<script lang="ts">
export default {
  name: "FrontendApp",
};
</script>

<script lang="ts" setup>
import FrontendNavigation from "@/frontend/components/Navigation/index.vue";
import AppNavigationHeader from "@/shared/components/AppNavigation/Header/index.vue";
import FrontendNavigationMobile from "@/frontend/components/Navigation/Mobile/index.vue";
import AppFooter from "@/shared/components/AppFooter/index.vue";
import AppEnvironment from "@/shared/components/AppEnvironment/index.vue";
import AppModal from "@/shared/components/AppModal/index.vue";
import OffCanvas from "@/shared/components/OffCanvas/index.vue";
import AppConfirm from "@/shared/components/AppConfirm/index.vue";
import AppNotifications from "@/shared/components/AppNotifications/index.vue";
import SecurePage from "@/frontend/components/core/SecurePage/index.vue";
import BackgroundImage from "@/shared/components/BackgroundImage/index.vue";
import { useI18n } from "@/shared/composables/useI18n";
import { useUpdates } from "@/frontend/composables/useUpdates";
import { useAppStore } from "@/frontend/stores/app";
import { useNavStore } from "@/shared/stores/nav";
import { useOverlayStore } from "@/shared/stores/overlay";
import { useI18nStore } from "@/shared/stores/i18n";
import { useSessionStore } from "@/frontend/stores/session";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useMobile } from "@/shared/composables/useMobile";
import { useComlink } from "@/shared/composables/useComlink";
import { useAhoy } from "@/frontend/composables/useAhoy";
import { useMe as useMeQuery } from "@/services/fyApi";
import FetchProgressBar from "@/shared/components/FetchProgressBar/index.vue";
import {
  BtnSizesEnum,
  BtnVariantsEnum,
} from "@/shared/components/base/Btn/types";
import { useAxiosInterceptors } from "@/frontend/composables/useAxiosInterceptors";
import { useCheckStoreVersion } from "@/shared/composables/useCheckStoreVersion";
import { useAppNotifications } from "@/shared/composables/useAppNotifications";
import { useWebpCheck } from "@/shared/composables/useWebpCheck";
import { useFlashNotifications } from "@/shared/composables/useFlashNotifications";
import { useMetaInfo } from "@/shared/composables/useMetaInfo";
import { useSupportPrompt } from "@/shared/composables/useSupportPrompt";

useWebpCheck(true);

useMetaInfo();

useAxiosInterceptors();

const mobile = useMobile();

useUpdates();

useAhoy();

const { requestBrowserPermission } = useAppNotifications();

const appStore = useAppStore();

const navStore = useNavStore();

const { collapsed: navCollapsed } = storeToRefs(navStore);

const overlayStore = useOverlayStore();

const { visible: overlayVisible } = storeToRefs(overlayStore);

const sessionStore = useSessionStore();

const { isAuthenticated } = storeToRefs(sessionStore);

const supportPrompt = useSupportPrompt();

const { t, availableLocales, currentLocale } = useI18n();

useFlashNotifications();

watch(
  () => navCollapsed.value,
  () => {
    setNoScroll();
  },
);

watch(
  () => overlayVisible.value,
  () => {
    setNoScroll();
  },
);

const router = useRouter();

watch(
  () => isAuthenticated.value,
  async () => {
    if (isAuthenticated.value) {
      await requestBrowserPermission();
      maybeCountLogin();
    } else if (route.meta.needsAuthentication) {
      await router.push({ name: "login" });
    }
  },
);

const maybeCountLogin = () => {
  if (supportPrompt.sessionAlreadyCountedLogin()) return;
  supportPrompt.markLoginCountedForSession();
  supportPrompt.notifyIfMilestone("logins", [5, 10, 25, 50], "loginMilestone");
};

const maybeCountVisit = () => {
  if (isAuthenticated.value) return;
  if (supportPrompt.sessionAlreadyCountedVisit()) return;
  supportPrompt.markVisitCountedForSession();
  supportPrompt.notifyIfMilestone("visits", [10, 25, 50], "visitMilestone");
};

const route = useRoute();

const comlink = useComlink();

watch(
  () => route.name,
  async () => {
    await checkSessionReload();

    setupLocale();
  },
);

useCheckStoreVersion(appStore);

const userUpdateComlink = ref();
const fleetCreateComlink = ref();
const fleetUpdateComlink = ref();

onMounted(async () => {
  await checkSessionReload();
  setNoScroll();

  if (isAuthenticated.value) {
    await requestBrowserPermission();
    maybeCountLogin();
  } else {
    maybeCountVisit();
  }

  userUpdateComlink.value = comlink.on("user-update", refetchCurrentUser);
  fleetCreateComlink.value = comlink.on("fleet-create", refetchCurrentUser);
  fleetUpdateComlink.value = comlink.on("fleet-update", refetchCurrentUser);

  setupLocale();
});

onUnmounted(() => {
  userUpdateComlink.value();
  fleetCreateComlink.value();
  fleetUpdateComlink.value();
});

const i18nStore = useI18nStore();

const { locale } = storeToRefs(i18nStore);

const setupLocale = () => {
  if (!locale.value && availableLocales().includes(navigator.language)) {
    i18nStore.locale = navigator.language;
  }
};

const setNoScroll = () => {
  if (!navCollapsed.value) {
    document.body.classList.add("nav-visible");
  } else {
    document.body.classList.remove("nav-visible");
  }

  if (!navCollapsed.value || overlayVisible.value) {
    document.body.classList.add("no-scroll");
  } else {
    document.body.classList.remove("no-scroll");
  }
};

const checkSessionReload = async () => {
  if (route.query.reload_session) {
    await sessionStore.fetchUserAndLogin();
  }
};

const { data: user, refetch: refetchCurrentUser } = useMeQuery({
  query: {
    retry: false,
  },
});

watch(
  () => user.value,
  () => {
    if (user.value) {
      sessionStore.login(user.value);
    }
  },
);

const localeMapping = {
  de: "Deutsch",
  en: "English",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  zh: "中文",
  "zh-CN": "中文 (简体)",
  "zh-TW": "中文 (繁體)",
};

const locales = computed(() => {
  return availableLocales() as (keyof typeof localeMapping)[];
});

const activeLocale = (locale: string) => {
  return (
    locale === currentLocale() ||
    (!locale.includes("zh") && locale === currentLocale().split("-")[0])
  );
};

const setLocale = (locale: string) => {
  i18nStore.locale = locale;
};
</script>

<template>
  <div
    :key="locale"
    :class="{
      [`page-${String(route.name)}`]: true,
    }"
    class="app-body"
  >
    <FetchProgressBar />
    <BackgroundImage />

    <div class="app-content">
      <transition name="fade" mode="out-in">
        <FrontendNavigation />
      </transition>
      <div class="main-wrapper">
        <div class="main-inner">
          <AppNavigationHeader />

          <router-view v-slot="{ Component, route: viewRoute }">
            <transition name="fade" mode="out-in">
              <SecurePage
                v-if="
                  viewRoute.meta.needsSecurityConfirm &&
                  !sessionStore.accessConfirmedDate
                "
              />
              <section
                v-else
                class="container main"
                :class="{
                  [route.name || '']: true,
                }"
              >
                <component
                  :is="Component"
                  :key="`${locale}-${viewRoute.path}`"
                />
              </section>
            </transition>
          </router-view>
        </div>

        <AppFooter
          :codename="appStore.codename"
          :version="appStore.version"
          :git-revision="appStore.gitRevision"
          :online="appStore.online"
        >
          <a
            v-tooltip="'Roberts Space Industries'"
            href="https://robertsspaceindustries.com/"
            target="_blank"
            rel="noopener"
          >
            RSI
          </a>
          |
          <router-link :to="{ name: 'privacy-policy' }">
            {{ t("nav.privacyPolicy") }}
          </router-link>
          |
          <router-link :to="{ name: 'impressum' }">
            {{ t("nav.impressum") }}
          </router-link>
          |
          <a href="https://api.fleetyards.net" target="_blank" rel="noopener">
            {{ t("nav.api") }}
          </a>
          |
          <BtnDropdown
            :text-inline="true"
            :inline="true"
            :size="BtnSizesEnum.SMALL"
            :variant="BtnVariantsEnum.LINK"
            :expand-top="true"
          >
            <template #label>
              <i class="fa-duotone fa-language" /> {{ currentLocale() }}
            </template>
            <Btn
              v-for="availableLocale in locales"
              :key="`locale-${availableLocale}`"
              :size="BtnSizesEnum.SMALL"
              :active="activeLocale(availableLocale)"
              @click="setLocale(availableLocale)"
            >
              {{ localeMapping[availableLocale] }} - {{ availableLocale }}
            </Btn>
          </BtnDropdown>
        </AppFooter>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <FrontendNavigationMobile v-if="mobile" />
    </transition>

    <AppConfirm />
    <AppModal />
    <OffCanvas />
    <AppNotifications />
    <AppEnvironment :git-revision="appStore.gitRevision" />
  </div>
</template>
